<template>
  <el-form ref="formRef" class="schema-search-form" :model="query" v-bind="formPropsC">
    <el-row :gutter="gutter" type="flex" justify="start" align="middle" class="schema-search-row">
      <el-col
        v-for="(field, idx) in visibleFields"
        :key="fieldKey(field, idx)"
        :span="resolveSpanByContainer(field)"
        :offset="field.component === 'actions' ? actionOffset : 0"
      >
        <component
          :is="'div'"
          v-if="field.component === 'actions'"
          class="schema-search-actions"
          :class="{ 'schema-search-actions--expanded': expanded }"
        >
          <el-button size="mini" type="primary" @click="$emit('search')">筛选</el-button>
          <el-button size="mini" @click="$emit('reset')">重置</el-button>
          <el-button v-if="hasAdvanced" size="mini" type="text" @click="$emit('update:expanded', !expanded)">
            {{ expanded ? '收起' : '更多条件' }}
          </el-button>
        </component>

        <el-form-item :label="field.label" class="schema-search-item">
          <el-input
            v-if="field.component === 'input'"
            v-model="query[field.prop]"
            class="schema-search-input"
            v-bind="field.componentProps"
            :placeholder="field.componentProps?.placeholder || '输入' + field?.label"
          ></el-input>

          <el-input-number
            v-else-if="field.component === 'number'"
            v-model="query[field.prop]"
            class="schema-search-input"
            v-bind="field.componentProps"
            :placeholder="field.componentProps?.placeholder || '输入' + field?.label"
          ></el-input-number>

          <el-select
            v-else-if="field.component === 'select'"
            v-model="query[field.prop]"
            class="schema-search-input"
            v-bind="field.componentProps"
            :placeholder="field.componentProps?.placeholder || '选择' + field?.label"
          >
            <el-option
              v-for="(opt, oIdx) in field.options || []"
              :key="opt.key + '_' + oIdx"
              :label="opt.label"
              :value="opt.value"
            ></el-option>
          </el-select>

          <DateTimeRangePicker
            v-else-if="field.component === 'datetime-range'"
            v-model="query[field.prop]"
            class="schema-search-input"
            v-bind="field.componentProps"
            style="width: 100%"
          />

          <el-date-picker
            v-else-if="field.component === 'date-range'"
            v-model="query[field.prop]"
            class="schema-search-input"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            v-bind="field.componentProps"
            style="width: 100%"
          />
          <PrependInput
            v-else-if="field.component === 'prepend-input'"
            v-model="query[field.prop]"
            class="schema-search-input"
            v-bind="field.componentProps"
            :options="field.options"
          />

          <slot v-else class="schema-search-input" :name="field.slot || field.prop" :query="query" :field="field" />
        </el-form-item>
      </el-col>
      <slot name="extra" :query="query" />
    </el-row>
  </el-form>
</template>

<script>
import DateTimeRangePicker from '@/components/DateTimeRangePicker'
import PrependInput from './searchItemComp/PrependInput'

const DEFAULT_SPAN = Object.freeze({ xs: 24, sm: 12, md: 8, lg: 6, xl: 6, xxl: 6 })
const DEFAULT_BREAKPOINTS = Object.freeze({ sm: 768, md: 992, lg: 1200, xl: 1600, xxl: 1920 })
const DEFAULT_FORM_PROPS = Object.freeze({ labelPosition: 'right', labelWidth: '80px', size: 'mini', inline: true })

export default {
  name: 'SchemaSearchRenderer',
  components: { DateTimeRangePicker, PrependInput },
  emits: ['search', 'reset', 'update:expanded'],
  props: {
    formProps: {
      type: Object,
      default: () => ({}),
    },
    query: {
      type: Object,
      required: true,
    },
    schema: {
      type: Array,
      default: () => [],
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    gutter: {
      type: Number,
      default: 16,
    },
    defaultSpan: {
      type: Object,
      default: () => ({ ...DEFAULT_SPAN }),
    },
    labelWidth: {
      type: String,
      default: '80px',
    },
    containerBreakpoints: {
      type: Object,
      default: () => ({ ...DEFAULT_BREAKPOINTS }),
    },
  },
  data() {
    return {
      containerWidth: 0,
      resizeObserver: null,
      removeWindowResizeListener: null,
    }
  },
  computed: {
    formPropsC() {
      return { ...DEFAULT_FORM_PROPS, ...this.formProps }
    },
    currentBreakpoint() {
      const w = this.containerWidth
      const bp = {
        sm: this.containerBreakpoints?.sm ?? DEFAULT_BREAKPOINTS.sm,
        md: this.containerBreakpoints?.md ?? DEFAULT_BREAKPOINTS.md,
        lg: this.containerBreakpoints?.lg ?? DEFAULT_BREAKPOINTS.lg,
        xl: this.containerBreakpoints?.xl ?? DEFAULT_BREAKPOINTS.xl,
      }
      if (w >= bp.xl) return 'xl'
      if (w >= bp.lg) return 'lg'
      if (w >= bp.md) return 'md'
      if (w >= bp.sm) return 'sm'
      return 'xs'
    },
    actionOffset() {
      const list = this.visibleFields
      const actionIndex = list.findIndex(f => f.component === 'actions')
      if (actionIndex < 0) return 0
      const actionField = list[actionIndex]
      const actionSpan = this.resolveSpanByContainer(actionField)
      // 计算 actions 所在行里，前面字段已占用的列数
      let used = 0
      for (let i = 0; i < actionIndex; i++) {
        const span = this.resolveSpanByContainer(list[i])
        if (used + span > 24) {
          used = span // 换到下一行
        } else {
          used += span
        }
      }
      // 如果本行放不下 actions，则 actions 自动换到新行再右对齐
      if (used + actionSpan > 24) {
        return Math.max(24 - actionSpan, 0)
      }
      return Math.max(24 - used - actionSpan, 0)
    },

    hasAdvanced() {
      return (this.schema || []).some(f => !!f && f.advanced)
    },
    visibleFields() {
      const list = (this.schema || []).filter(Boolean)
      const resolved = list.filter(f => {
        if (typeof f.hidden === 'function' && f.hidden(this.query)) return false
        if (!this.expanded && f.advanced) return false
        return true
      })
      // 确保 actions 永远在最后
      const actions = resolved.filter(f => f.component === 'actions')
      const rest = resolved.filter(f => f.component !== 'actions')
      return [...rest, ...actions]
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.bindContainerResize()
      this.updateContainerWidth()
    })
  },
  beforeDestroy() {
    this.unbindContainerResize()
  },
  methods: {
    getContainerEl() {
      const form = this.$refs.formRef
      const formEl = form && form.$el ? form.$el : form
      return formEl?.parentElement || formEl || null
    },
    updateContainerWidth() {
      const container = this.getContainerEl()
      this.containerWidth = container?.clientWidth || 0
    },
    bindContainerResize() {
      const container = this.getContainerEl()
      if (!container) return

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.updateContainerWidth())
        this.resizeObserver.observe(container)
        return
      }

      if (typeof window !== 'undefined') {
        const onResize = () => this.updateContainerWidth()
        window.addEventListener('resize', onResize)
        this.removeWindowResizeListener = () => window.removeEventListener('resize', onResize)
      }
    },
    unbindContainerResize() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      if (this.removeWindowResizeListener) {
        this.removeWindowResizeListener()
        this.removeWindowResizeListener = null
      }
    },
    fieldKey(field, idx) {
      const base = field?.key || field?.prop || field?.slot || field?.component || 'field'
      return String(base) + '_' + idx
    },
    resolveSpan(field) {
      const span = field && field.span ? field.span : this.defaultSpan
      return {
        xs: span.xs ?? DEFAULT_SPAN.xs,
        sm: span.sm ?? DEFAULT_SPAN.sm,
        md: span.md ?? DEFAULT_SPAN.md,
        lg: span.lg ?? DEFAULT_SPAN.lg,
        xl: span.xl ?? DEFAULT_SPAN.xl,
      }
    },
    resolveSpanByContainer(field) {
      const span = this.resolveSpan(field)
      const bp = this.currentBreakpoint
      return span[bp] ?? DEFAULT_SPAN[bp] ?? 24
    },
  },
}
</script>

<style lang="scss" scoped>
.schema-search-row {
  flex-wrap: wrap;
}
.schema-search-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  height: 100%;
}
.schema-search-item {
  margin-bottom: 10px;
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  ::v-deep .el-form-item__label {
    white-space: nowrap;
  }
  ::v-deep .el-form-item__content {
    flex: 1;
  }
}
.schema-search-input {
  width: 100%;
}
</style>
